import { useState } from "react";
import { ActionModal, Field } from "@cntrl/ui";
import { useUpdateThread, type ThreadDetail } from "../../app/hooks/useInbox.js";
import { useAvatarUrl, useClearAvatar, useSetAvatar } from "../../app/hooks/useAvatar.js";
import { errorMessage } from "../../app/hooks/apiError.js";
import { threadTitle } from "../../app/hooks/inboxFormat.js";
import { AvatarEditor } from "../shared/AvatarEditor.js";
import styles from "./Inbox.module.scss";

export interface EditChatModalProps {
  thread: ThreadDetail;
  /** The derived default name, so the field can offer it as a placeholder rather than pre-typing it. */
  derivedName: string;
  onClose: () => void;
}

/**
 * Rename a chat / change its picture (ART-159, PO 2026-09-11). One dialog behind the header's Edit door —
 * the whole subject block of the chat head — because renaming and re-photoing are two halves of one intent
 * (the handoff's own shape: "a single Edit chat button owning both rename and re-photo").
 *
 * **EVERY chat has a picture, group chats included, and NO BAND is involved.** The bytes go to the global
 * avatar store (`media.avatars`) through a route with no tenant in its path, gated by the same audience that
 * may rename the chat. That is what makes a group chat's picture possible at all — it has no show and no
 * band to file under — and what makes a cross-band chat's picture visible to both sides.
 *
 * **It is also why nothing here reaches for a band context.** The previous build uploaded through
 * `/bands/{bandId}/media`, so this dialog needed a band; it opens from the TOPBAR, outside the artist
 * shell's BandProvider, and pressing Edit on a group chat took the whole app to its error boundary reading
 * "useUploadMedia needs a band". The fix was not a better band to pick — it was that an identity picture
 * never had a tenant to belong to.
 *
 * **The picture uses `AvatarEditor` — the profile's control, not a second one** (PO: "make it like the user
 * profile's avatar editor, we need a consistent design language"). Everything about the gesture is therefore
 * identical: the control IS the picture, hover reads "Replace", the SE badge carries the pencil, a pick goes
 * through the crop, and — the part that changes this file's shape — **the picture uploads IMMEDIATELY rather
 * than riding Save**, exactly as a profile avatar does. Only the NAME is a form field, so Save is about the
 * name alone. The one departure is the SHAPE: a chat is a rounded square, because a group is not somebody's
 * face (the same rule `PersonAvatar` enforces on the read-only side).
 *
 * **The field is EMPTY on a chat nobody has renamed, and the derived name is its placeholder.** Pre-typing
 * "Eastside Bowl · C Party" into the box would make every save a rename — the first person to open this and
 * press Save would freeze today's derived name forever, and the chat would stop following its show. Empty
 * means "no name of my own", which is exactly what the server stores, so clearing the box gives it back.
 * The placeholder is the whole explanation, which is why no hint line sits under it (PO, 2026-09-11).
 *
 * **It never renders for a caller who may not edit** — the header only mounts it on the SERVED `canEdit`
 * verdict, and the route refuses independently. Both halves, because an affordance that is refused and a
 * permission that is unreachable are the same defect twice.
 */
export function EditChatModal({ thread, derivedName, onClose }: EditChatModalProps) {
  const threadId = thread.threadId as string;

  const [title, setTitle] = useState(thread.title ?? "");
  const [uploading, setUploading] = useState(false);

  const save = useUpdateThread(threadId);

  return (
    <ActionModal
      title="Edit chat"
      confirmLabel="Save"
      loading={save.isPending}
      confirmDisabled={uploading}
      onCancel={onClose}
      // Save is about the NAME. The picture has already landed by the time anybody reaches this — it
      // uploads on pick, exactly as a profile avatar does.
      onConfirm={() => save.mutate({ title: title.trim() }, { onSuccess: () => onClose() })}
    >
      <ChatPhotoField thread={thread} busy={uploading} onBusyChange={setUploading} />

      <Field
        label="Chat name"
        value={title}
        placeholder={derivedName}
        maxLength={80}
        onChange={(e) => setTitle(e.target.value)}
      />

      {save.isError && (
        <p className={styles.dialogError} role="alert">
          {errorMessage(save.error, "Couldn't save the chat.")}
        </p>
      )}
    </ActionModal>
  );
}

/**
 * The chat's PICTURE — the global avatar store, uploaded and cleared through the tenant-less avatar route.
 *
 * A CHILD component, and now for a much smaller reason than before: it owns the upload state and the "did
 * that land" error, and keeping them here means the parent's Save is about the NAME alone. It no longer
 * needs a band, because an identity picture no longer has a tenant (see this file's header).
 *
 * The control is `AvatarEditor` — the PROFILE's control, not a second one (PO: "make it like the user
 * profile's avatar editor, we need a consistent design language"). So the gesture is identical: the control
 * IS the picture, hover reads "Replace", the SE badge carries the pencil, a pick goes through the crop, and
 * the picture uploads IMMEDIATELY rather than riding Save, exactly as a profile avatar does. The one
 * departure is the SHAPE: a chat is a rounded square, because a group is not somebody's face (the same rule
 * `PersonAvatar` enforces on the read-only side).
 *
 * `onBusyChange` reaches back up because Save belongs to the PARENT and must not fire over an upload in
 * flight — the dialog would close on a picture that is still moving.
 */
function ChatPhotoField({
  thread,
  busy,
  onBusyChange,
}: {
  thread: ThreadDetail;
  busy: boolean;
  onBusyChange: (busy: boolean) => void;
}) {
  const threadId = thread.threadId as string;
  const [photoError, setPhotoError] = useState<string | null>(null);
  // Both reseed the thread read, which carries the avatarMediaId every surface renders by — so the head and
  // the sidebar row repaint without either of them knowing this dialog exists.
  const setAvatar = useSetAvatar("thread", threadId, ["thread", threadId]);
  const clearAvatar = useClearAvatar("thread", threadId, ["thread", threadId]);

  // Only the chat's OWN picture is shown as the current value. The header falls back to the show's flyer
  // when there is none, and that fallback is deliberately NOT rendered here: it would read as a picture this
  // dialog had set, and "Remove picture" beside it would seem to promise deleting the show's flyer.
  const preview = useAvatarUrl("thread", threadId, thread.avatarMediaId);

  async function onPicked(file: File) {
    setPhotoError(null);
    onBusyChange(true);
    try {
      await setAvatar.mutateAsync(file);
    } catch (err) {
      // Surfaced HERE rather than toasted away: the picture the reader just chose did not land, and the
      // dialog is still open in front of them.
      setPhotoError(errorMessage(err, "Couldn't upload that picture."));
    } finally {
      onBusyChange(false);
    }
  }

  return (
    <>
      <AvatarEditor
        src={preview}
        placeholder="#"
        shape="rounded"
        label="Replace chat picture"
        removeLabel="Remove chat picture"
        busy={busy}
        onPicked={(file) => void onPicked(file)}
        // Only when the chat has a picture OF ITS OWN. The control's `src` falls back to the show's flyer,
        // which this dialog has no business deleting — so the question is asked of the thread, not the src.
        onRemove={thread.avatarMediaId ? () => clearAvatar.mutate() : undefined}
      />
      {photoError && (
        <p className={styles.dialogError} role="alert">
          {photoError}
        </p>
      )}
    </>
  );
}

/** The derived default for a thread — what the name field offers as its placeholder. Takes the same
 *  row-shaped subject the header builds (`participantPersonIds`), not the raw detail. */
export function derivedChatName(
  thread: ThreadDetail & { participantPersonIds?: string[] },
  ctx?: { names?: ReadonlyMap<string, string>; myPersonId?: string | null },
): string {
  // `title` is deliberately stripped: this is the name the chat would have if nobody had renamed it, which
  // is the thing the placeholder is promising to give back.
  return threadTitle({ ...thread, title: null }, ctx);
}
